import { pageTypeConfig } from "senf-atomic-design-system/src/components/special/survey/pageTypes";
import type {
  Question,
  QuestionOption,
  Result,
  ValueTypes,
  Values,
} from "senf-atomic-design-system/src/components/special/survey/survey.types";
import { exportXlsx, getAgeGroup, parseGeoData } from "senf-shared";
import type { Project, Survey } from "senf-shared/types";
import type { Sheet } from "senf-shared/utils/exportData";
import type { WorkBook } from "xlsx-js-style";

import { getAlpha } from "senf-shared/utils/helpers";
import i18n from "senf-shared/utils/i18n";
import {
  prepareQuestionMapResults,
  resolveResultIds,
} from "../../../../util/geojsonHelpers";

/**
 * Retrieves the values from the provided question options based on the answer.
 *
 * @param question - The question options.
 * @param answer - The answer value.
 * @returns The corresponding values from the question options.
 * @legacy This function still checks for the value property which will not be used to store answers.
 */
const getValue = (options?: QuestionOption[], answer?: Values) => {
  const getFallbackValue = (e?: { value: string }, i?: number) =>
    (e?.value ||
      (i != null && i > -1 ? `${i18n.t("global:option")} ${i + 1}` : " ")) ??
    " ";

  if (Array.isArray(answer)) {
    return (
      options
        ?.filter((e) => answer?.includes(e.uid) || answer?.includes(e.value))
        ?.map(getFallbackValue) || []
    );
  }
  const index = options?.findIndex(
    (e) => answer === e.uid || answer === e.value,
  );
  const option = index != null ? options?.[index] : undefined;
  return [getFallbackValue(option, index) || answer || " "];
};

const getRankingValue = (
  rows: QuestionOption[] | undefined,
  qValue: Values,
  rowIndex: number,
  isInNumbers: boolean,
): number | string => {
  if (!Array.isArray(qValue)) return isInNumbers ? -1 : " ";
  const numberIndex = qValue.findIndex((e) => e === rows?.[rowIndex]?.uid);
  return isInNumbers
    ? numberIndex > -1
      ? numberIndex + 1
      : " "
    : (rows?.find((e) => e.uid === qValue[rowIndex])?.value ?? " ");
};

/**
 * Checks if the provided question contains the answer.
 *
 * @param question - The question option.
 * @param answer - The answer value.
 * @param isInNumbers - Indicates whether the answer is in numbers.
 * @returns 1 if the question contains the answer, null otherwise.
 * @legacy This function still checks for the value property which will not be used to store answers.
 */
function questionContainsAnswer(
  option: QuestionOption,
  answer: Values,
  isInNumbers: true,
  inArray?: boolean,
): "1" | "0";
function questionContainsAnswer(
  option: QuestionOption,
  answer: Values,
  isInNumbers?: false,
  inArray?: boolean,
): string;
function questionContainsAnswer(
  option: QuestionOption,
  answer: Values,
  isInNumbers?: boolean,
  inArray?: boolean,
) {
  const value = isInNumbers ? "1" : option?.value || (inArray ? "" : " ");
  const not = isInNumbers ? "0" : inArray ? "" : " ";
  if (Array.isArray(answer)) {
    return answer.includes(option?.uid || "") ||
      answer.includes(option?.value || "")
      ? value
      : not;
  }
  return answer === option?.uid || answer === option?.value ? value : not;
}

type HeaderCol = {
  questionLong: string;
  questionShort: string;
  questionNum: string;
};
type ValueCol = {
  result: string;
};
/**
 * Constructs a header cell object containing the question text and short form.
 *
 * @param question - The question object.
 * @returns The header cell object with the question text and short form.
 */
const headerCell = (
  question: Question & { index: number },
  props?: {
    row?: {
      index: number;
      value: string;
      length: number;
    };
    column?: {
      index: number;
      value: string;
      length: number;
    };
  },
): HeaderCol => {
  const qtext: (string | undefined)[] = [question.qtext];
  const { row, column } = props || {};

  // const optionText = question.qtype === "ratingScale" ? optText || `${i18n.t("global:option")} ${optNum || 0 + 1}` : optText;
  if (row?.index) qtext.push(row.value);

  const columnText =
    question.qtype === "ratingScale"
      ? column?.value || `${i18n.t("global:option")} ${column?.index || 0 + 1}`
      : column?.value;
  if (row?.index && column?.index) qtext.push(columnText);

  const short: (string | undefined)[] = [`Q${question.index}`];
  if (row?.index && row?.length > 1) short.push(`${row?.index}`);
  if (row?.index && column?.index && column?.length > 1)
    short.push(`${column?.index}`);
  return {
    questionLong: qtext.filter(Boolean).join(" – "),
    questionShort: qtext[0] || " ",
    questionNum: short.filter(Boolean).join("_"),
  };
};
/**
 * Converts data from pages, values, and isInNumbers flag into a table format.
 *
 * @param pages - The array of pages.
 * @param isInNumbers - Indicates whether the answer values are in numbers.
 * @param result - The result object containing question values and timestamps.
 * @returns The data converted into a table format.
 */
export const convertDataToTable = (
  project: Project,
  pages: Question[],
  isInNumbers: boolean,
  rowResult?: Result,
): (HeaderCol & ValueCol)[] => {
  return pages?.flatMap((q, i) => {
    if (pageTypeConfig[q.qtype]?.structuralPage) return [];
    // Make a copy of the current question
    const question = { ...q, index: q.index || i };
    const { uid, options, columns } = question;
    const { values } = rowResult || {};
    const qValue = values?.[uid]?.value;

    // Determine the question type
    const isRadioMatrix =
      question.qtype === "radioMatrix" && !question.multipleChoice;
    const isCheckboxMatrix =
      question.qtype === "radioMatrix" && question.multipleChoice;

    const isCheckbox =
      (question.qtype === "radio" && question.multipleChoice) ||
      (question.qtype === "imageChoice" && question.multipleChoice);

    const isRadio =
      (question.qtype === "radio" && !question.multipleChoice) ||
      (question.qtype === "imageChoice" && !question.multipleChoice);

    const isText = question.qtype === "text";
    const isNumber = question.qtype === "number";

    const isNumericRating =
      question.qtype === "stars" || question.qtype === "slider";

    const isRatingScale = isNumericRating || question.qtype === "ratingScale";

    const isRanking = question.qtype === "ranking";

    const isMapBased =
      question.qtype === "point" ||
      question.qtype === "path" ||
      question.qtype === "area";

    if (isMapBased) {
      const headerBase = headerCell(question);

      // Get the value for the regular question
      const geoData = values?.[uid]?.geoData
        ? parseGeoData(values?.[uid].geoData)
        : null;
      const row = { ...values?.[uid], ...rowResult };
      const result = geoData
        ? JSON.stringify(
            prepareQuestionMapResults(
              { geoData, question, rowResult: row },
              true,
            ),
          )
        : " ";

      const transformColumns =
        [
          question.mapbasedQtype === "radio" &&
            ({ label: "Options", type: "choices" } as const),
          question.mapbasedQtype === "textarea" &&
            ({ label: "Comment", type: "description" } as const),
          ...(question?.resultsTableColumns?.map(
            (e) => ({ ...e, type: "tags" }) as const,
          ) || []),
        ]
          ?.filter((e) => !!e)
          .map((column) => {
            let geoDataValues;
            if (column.type === "tags") {
              geoDataValues = geoData?.features
                .map((e) => {
                  const resolvedIds = resolveResultIds({ ...e, question });
                  return resolvedIds.tags?.find(
                    (e) => e.columnId === column.value,
                  );
                })
                ?.filter(Boolean)
                ?.map((e) => e?.value)
                ?.join(", ");
            } else if (column.type === "choices") {
              geoDataValues = geoData?.features
                .map((e) => resolveResultIds({ ...e, question }))
                ?.map((e) => e.value)
                .join(", ");
            } else if (column.type === "description") {
              // console.log(geoData?.features)
              geoDataValues = geoData?.features
                ?.map((e) => e.properties?.description)
                ?.filter(Boolean)
                ?.join(", ");
            }
            return {
              column,
              result: geoDataValues || " ",
            };
          }) || [];

      const extraColumns = transformColumns.map(({ column, result }, index) => {
        return {
          questionLong: `${headerBase.questionLong} (${column.label})`,
          questionShort: `${headerBase.questionShort} (${column.label})`,
          questionNum: `${headerBase.questionNum}_${index + 2}`,
          result,
        };
      });

      const header = extraColumns?.length
        ? headerCell(question, {
            row: {
              index: 1,
              value: "GIS-Data",
              length: extraColumns?.length + 1,
            },
          })
        : headerBase;

      const c = [{ ...header, result }, ...extraColumns].filter(Boolean);
      return c;
    }

    const headerBase = headerCell(question);
    const extraColumns =
      question?.resultsTableColumns?.map((column) => {
        const entryTag = values?.[uid]?.tags?.find(
          (tag) => tag.key === column.value,
        );
        const entryTagLabel = (tagValue: string) =>
          column?.values?.find((tag) => tag?.value === tagValue)?.label ||
          tagValue ||
          " ";

        return {
          label: column.label,
          result: entryTagLabel(entryTag?.value) as ValueTypes,
          value: column.value,
          uid: column.value,
          transform: entryTagLabel,
        };
      }) || [];

    const simpleTextOrNumberWithTags =
      (isText || isNumber) && question.options?.length === 1;

    const multipleTextOrNumberWithTags =
      (isText || isNumber) && (question.options?.length || 0) > 1;

    /**
     * 3D matrix pages are pages with multiple options and columns.
     * They need three dimensions so that for:
     * - 1. each question,
     * - 2. each option and
     * - 3. each column
     * you have a cell in the table.
     * This results in a table like this
     * | Q1 – Option 1 – Column 1 | Q1 – Option 1 – Column 2 | Q1 – Option 2 – Column 1 | Q1 – Option 2 – Column 2 |
     * |--------------------------|--------------------------|--------------------------|--------------------------|
     * |                        1 |                          |                        1 |                          |
     */
    const is3D =
      (isInNumbers && (isCheckboxMatrix || isRadioMatrix || isRatingScale)) ||
      multipleTextOrNumberWithTags;

    if (is3D) {
      let opts = [...(options || [])];
      let cols = [...(columns || [])];
      if (multipleTextOrNumberWithTags) {
        opts = opts?.map((e) => ({
          ...e,
          value: e.rowTitle?.value || e.value,
        }));
        cols = [
          {
            value: i18n.t("global:answer"),
            uid: "value",
          },
          ...extraColumns.map((e) => ({
            ...e,
            value: e.label || e.value,
          })),
        ];
      }
      // Generate cells for each combination of options and columns
      const c =
        opts?.flatMap((opt, r, optArr) => {
          let optValue = opt.label || opt.value;
          if (isRatingScale) {
            optValue =
              opt.rowTitle?.value ||
              opt.labels
                ?.map((e) => e.value)
                .filter(Boolean)
                .join(" → ") ||
              (optArr.length > 1 ? getAlpha(r) : "");
          }

          return (
            cols?.map((col, c, colArr) => {
              let colValue = col.label || col.value;
              if (isRatingScale) {
                colValue = col.value || c + 1;
              }
              // Create the header cell for the current combination
              const header = headerCell(question, {
                row: {
                  index: r + 1,
                  value: optValue,
                  length: optArr?.length,
                },
                column: {
                  index: c + 1,
                  value: colValue,
                  length: colArr?.length,
                },
              });

              // Check if the combination is selected in the answer
              let result =
                qValue && typeof qValue === "object" && !Array.isArray(qValue)
                  ? questionContainsAnswer(col, qValue?.[opt.uid], isInNumbers)
                  : " ";

              if (
                qValue &&
                typeof qValue !== "string" &&
                !Array.isArray(qValue) &&
                qValue[opt.uid]
              ) {
                if (isText || isNumber) {
                  if (col.uid === "value") {
                    console.log(qValue[opt.uid]);
                    result =
                      qValue[opt.uid]?.translations?.[
                        project?.settings?.originalLanguage
                      ] || qValue[opt.uid]?.value;
                  } else {
                    result = col.transform(
                      qValue[opt.uid]?.tags?.find((e) => e.key === col.uid)
                        ?.value,
                    );
                  }
                }
              }

              return {
                ...header,
                result,
              };
            }) || []
          );
        }) || [];

      if (question.textResponse) {
        const header = headerCell(question, {
          row: {
            index: (options?.flat()?.length || 0) + 1,
            value: i18n.t("global:comment"),
            length: (options?.flat()?.length || 0) + 1,
          },
        });

        c.push({
          ...header,
          result:
            values?.[uid]?.translations?.[
              project?.settings?.originalLanguage
            ] ||
            values?.[uid]?.comment ||
            " ",
        });
      }
      return c;
    }

    const is2D =
      (!isInNumbers && (isRadioMatrix || isCheckboxMatrix || isRatingScale)) ||
      (isInNumbers && (isCheckbox || isRadio)) ||
      simpleTextOrNumberWithTags ||
      isRanking;

    if (is2D) {
      const cols = [...(options || [])];
      if (isRanking && !isInNumbers) {
        cols.splice(0, question.limitRanking);
      }
      if (question.otherOption) {
        cols.push({
          uid: "other",
          value: question.otherTitle || i18n.t("global:other"),
        } as QuestionOption);
      }
      if (question.otherOption && isInNumbers) {
        cols.push({
          uid: "otherValue",
          value: `${
            question.otherTitle || i18n.t("global:other")
          } – ${i18n.t("global:answer")}`,
        } as QuestionOption);
      }
      if (question.textResponse) {
        cols.push({
          uid: "textResponse",
          value: i18n.t("global:comment"),
        } as QuestionOption);
      }
      cols.push(...extraColumns);

      // Process 2D matrix question
      const c =
        cols
          .filter((e) => !!e)
          .map((opt, r, arr) => {
            // Create the header cell for the option
            let headerValue = opt.label || opt.value;
            if (isRatingScale) {
              headerValue =
                opt.rowTitle?.value ||
                opt.labels
                  ?.map((e) => e.value)
                  .filter(Boolean)
                  .join(" → ") ||
                getAlpha(r);
            } else if (isRanking && !isInNumbers) {
              headerValue = `#${r + 1}`;
            } else if (isText || isNumber) {
              headerValue =
                opt.rowTitle?.value || arr.length > 1 ? getAlpha(r) : "";
            }

            const header = headerCell(question, {
              row: {
                index: r + 1,
                value: headerValue || " ",
                length: arr?.length || 0,
              },
            });

            let result: string | 1 | 0 = " ";

            if (
              qValue &&
              typeof qValue !== "string" &&
              !Array.isArray(qValue) &&
              qValue[opt.uid]
            ) {
              if (isText || isNumber) {
                result =
                  qValue[opt.uid]?.translations?.[
                    project?.settings?.originalLanguage
                  ] || qValue[opt.uid]?.value;
              } else {
                const cols = isNumericRating
                  ? question.columns?.map((e, i) => ({
                      ...e,
                      value: i + 1,
                    }))
                  : question.columns;

                result =
                  cols
                    ?.map((col) =>
                      questionContainsAnswer(
                        col,
                        qValue[opt.uid],
                        isInNumbers,
                        true,
                      ),
                    )
                    .filter(Boolean)
                    .join(", ") || " ";
              }
            } else if (
              (qValue && typeof qValue === "string") ||
              Array.isArray(qValue)
            ) {
              // Check if the option is selected in the answer
              result = isRanking
                ? getRankingValue(options, qValue, r, isInNumbers)
                : questionContainsAnswer(opt, qValue, isInNumbers);
            }

            if (opt.uid === "otherValue" || opt.uid === "textResponse") {
              result =
                values?.[uid]?.translations?.[
                  project?.settings?.originalLanguage
                ] ||
                values?.[uid]?.comment ||
                " ";
            }
            if (opt.result) {
              result = opt.result;
            }

            return {
              ...header,
              result,
            };
          }) || [];

      return c;
    }

    // Process regular question

    // Get the value for the regular question
    const result =
      typeof qValue === "string" || Array.isArray(qValue)
        ? getValue(
            [
              ...(question.options || []),
              ...(question.otherOption
                ? [
                    {
                      uid: "other",
                      value:
                        values?.[uid]?.translations?.[
                          project?.settings?.originalLanguage
                        ] ||
                        values?.[uid]?.comment ||
                        question.otherTitle ||
                        i18n.t("global:other")!,
                    },
                    // @todo: maybe add otherValue value (if numeric export)
                  ]
                : []),
            ],
            qValue,
          )
        : " ";

    const c = extraColumns?.length
      ? [{ result }, ...extraColumns].map((e, i) => ({
          ...headerCell(question, {
            row: {
              index: i + 1,
              value: e.label ? `Tags (${e.label})` : " ",
              length: extraColumns?.length + 1,
            },
          }),
          result: e.result,
        }))
      : [{ ...headerBase, result }];

    return c;
  });
};

function calculateTimeDifference(row: Result) {
  // Create an array to store all the timestamp values
  const timestampArray = [];

  // Regular expression for ISO 8601 timestamp format
  const iso8601Regex = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z)$/;

  // Extract and push validated timestamp values to the array
  const timestamps =
    row?.values &&
    Object.values(row.values).filter((value) => value?.timeStamp);

  timestamps?.forEach((value) => {
    if (iso8601Regex.test(value.timeStamp)) {
      timestampArray.push(new Date(value.timeStamp));
    }
  });

  // Push the createdAt timestamp after validation
  if (iso8601Regex.test(row.createdAt)) {
    timestampArray.push(new Date(row.createdAt));
  }

  if (timestampArray.length === 0) {
    // No valid timestamps found
    return "0"; // or any other appropriate value
  }

  // Find the earliest and latest timestamps
  const earliestTimestamp = new Date(Math.min(...timestampArray));
  const latestTimestamp = new Date(Math.max(...timestampArray));

  // Calculate the time difference
  const timeDifference = latestTimestamp - earliestTimestamp;
  const hours = Math.floor(timeDifference / (1000 * 60 * 60));
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

export const initCustomDemographicsColumns = (project: Project) => {
  const customFields =
    project?.settings?.demographicData?.fields?.reduce<
      Record<string, Record<string, string>>
    >((acc, curr) => {
      if (curr.options) {
        acc[curr.name] = curr.options.reduce<Record<string, string>>(
          (optionsAcc, option) => {
            optionsAcc[option.value] = option.label;
            return optionsAcc;
          },
          {},
        );
      }
      return acc;
    }, {}) || {};

  const customColumn = (
    e: Project["settings"]["demographicData"]["fields"][number],
  ) => ({
    key: e.name,
    label: e.label,
    value: (row: Result) => {
      const val = row?.userData?.[e.name];
      if (e.type === "text") {
        return typeof val === "number" ? val.toString() : val || " ";
      }
      return val ? customFields[e.name][val] : " ";
    },
  });
  return { customFields, customColumn };
};
export const addDemographicsColumns = (
  project: Project,
  metaColumns: {
    key: string;
    label: string;
    value?: (row: Result) => string;
  }[],
) => {
  const { t } = i18n;
  const withDemographics =
    project?.settings?.demographicData?.collect !== false;

  if (!withDemographics) return;

  const { customColumn } = initCustomDemographicsColumns(project);

  metaColumns.push(
    {
      key: "age",
      label: t("global:age"),
      value: (row) => {
        const age = row.userData?.age || row.age;
        return age && !Number.isNaN(age) ? age?.toString() : " ";
      },
    },
    {
      key: "ageGroup",
      label: t("global:ageGroup"),
      value: (row) => {
        const age = row.userData?.age || row.age;
        return age && !Number.isNaN(age) ? getAgeGroup(age) : " ";
      },
    },
    {
      key: "gender",
      label: t("global:gender"),
      value: (row) =>
        row.userData?.gender || row?.gender
          ? t(`global:${row.userData?.gender || row?.gender}`)
          : " ",
    },
    {
      key: "postcode",
      label: t("global:postcode"),
      value: (row) => row.userData?.postcode || row.userPostcode || " ",
    },
    ...(project?.settings?.demographicData?.fields?.map(customColumn) || []),
  );
};
/**
 * Returns "2024-11-27 14:30"
 */
const formatDate = (date: Date) =>
  new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .format(date)
    .replace(",", "");

export const mapResults = (
  project: Project,
  results: Result[],
  survey: Survey,
  options: {
    isInNumbers?: boolean;
    excludeQTypes?: string[];
    excludeColumns?: string[];
  } = {},
) => {
  const { t } = i18n;
  const { isInNumbers, excludeQTypes, excludeColumns } = options;
  const pages = survey.pages
    .map((e, index) => ({ ...e, index: e.index || index }))
    .filter((e) => {
      return !excludeQTypes?.length || !excludeQTypes.includes(e.qtype);
    });

  const metaColumns: {
    key: string;
    label: string;
    value: (row: Result) => string;
  }[] = [
    {
      key: "submissions",
      label: t("global:submissions"),
      value: (row) => (row.completed ? t("global:yes") : t("global:no")),
    },
    {
      key: "date",
      label: `${t("global:date")}, ${t("global:clockTime")}`,
      value: (row) => formatDate(new Date(row.createdAt)),
    },
    // {
    // key: 'week',
    //   label: t("global:week"),
    //   value: row => calculateSubmissionWeek(row.createdAt, startDate, endDate, t),
    // },
    {
      key: "editingTime",
      label: t("global:editingTime"),
      value: (row) => calculateTimeDifference(row),
    },
    {
      key: "device",
      label: t("system:device"),
      value: (row) => t(`system:${row.device}`),
    },
    {
      key: "user-id",
      label: "User-ID",
      value: (row) => row.userId,
    },
    // {
    // key: 'username',
    //   label: t("global:username");
    //   value: row => row?.userData?.handle,
    // },
    // {
    // key: 'registrationDate',
    //   label: t("global:registrationDate");
    //   value: row => row?.userData?.registrationDate,
    // },
  ];

  addDemographicsColumns(project, metaColumns);

  const { headerCols, valueCols } = metaColumns
    .filter((e) => !(excludeColumns || []).includes(e.key))
    .reduce(
      (acc, curr) => {
        acc["headerCols"] ??= [];
        acc["headerCols"].push(curr.label);
        acc["valueCols"] ??= [];
        acc["valueCols"].push(curr.value);
        return acc;
      },
      {} as {
        headerCols: string[];
        valueCols: ((row: Result) => string)[];
      },
    );

  const convertHeader = (
    key: "questionLong" | "questionShort" | "questionNum" = "questionLong",
  ) => {
    return [
      ...headerCols,
      ...convertDataToTable(project, pages, !!isInNumbers).map(
        (q) => q[key] || " ",
      ),
    ];
  };

  const responses = results.map((row) => {
    return [
      ...valueCols.map((e) => e(row)),
      ...convertDataToTable(project, pages, !!isInNumbers, row).map(
        (q) => q?.result,
      ),
    ];
  });

  return {
    headerLong: convertHeader("questionLong"),
    headerShort: convertHeader("questionShort"),
    headerNum: convertHeader("questionNum"),
    responses,
  };
};
interface Props {
  project: Project;
  results: Result[];
  survey: Survey;
  fileName?: string;
  numbers?: boolean;
  download?: boolean;
}
export const exportSurveyResults = ({
  project,
  results,
  survey,
  fileName,
  numbers = false,
  download = true,
}: Props) => {
  return new Promise<WorkBook>((resolve, reject) => {
    (async () => {
      try {
        const { responses, headerLong, headerShort, headerNum } = mapResults(
          project,
          results,
          survey,
          {
            isInNumbers: numbers,
          },
        );
        const main: Sheet = {
          header: headerShort,
          data: [headerNum, ...responses].filter(Boolean),
          name: i18n.t("survey:results"),
          mergeQuestionCells: true,
          headerRows: 2,
        };
        const headerReference: Sheet = {
          header: headerLong,
          data: [headerNum],
          name: i18n.t("survey:builder.headerReference"),
        };
        const sheets = [main, headerReference];

        const workbook = await exportXlsx({
          fileName:
            `${fileName}${numbers ? "-numeric" : ""}` ||
            i18n.t("survey:results"),
          sheets,
          download,
        });

        resolve(workbook);
      } catch (err) {
        console.error(err);
        reject(
          "Unfortuately an error occured when downloading the file. Please try again or contact us.",
        );
      }
    })();
  });
};
